<template>
  <!-- Role switcher shown in the header (desktop) -->
  <div v-if="roleOptions.length > 1" class="ml-2">
    <treeselect
      v-model="selectedRoleId"
      :options="roleOptions"
      :disabled="isLoading"
      :clearable="false"
      class="header-role-treeselect rounded"
      style="background: #fff; min-width: 150px; max-width: 140px"
      :normalizer="normalizer"
      @select="onRoleSelected"
    />
  </div>
</template>

<script>
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import axiosR from '@/core/services/api/admin/roles'

export default {
  name: 'DropdownRole',
  data() {
    return {
      userData: this.safeGetUserData(),
      isLoading: false,
      selectedRoleId: null,
      normalizer(node) {
        return {
          id: Number(node.id),
          label: node.name
        }
      }
    }
  },
  computed: {
    roleOptions() {
      const roles = Array.isArray(this.userData?.roles) ? this.userData.roles : []
      return roles.map((role) => ({
        id: Number(role.id),
        name: role.name
      }))
    }
  },
  mounted() {
    this.selectedRoleId = this.userData?.roles?.[0]?.id
      ? Number(this.userData.roles[0].id)
      : null

    // Keep menu/labels in sync with role switch done by this component.
    window.addEventListener('role-changed', this.syncFromStorage)
  },
  beforeDestroy() {
    window.removeEventListener('role-changed', this.syncFromStorage)
  },
  methods: {
    safeGetUserData() {
      try {
        return JSON.parse(localStorage.getItem('userData')) || null
      } catch (e) {
        return null
      }
    },
    syncFromStorage() {
      this.userData = this.safeGetUserData()
      this.selectedRoleId = this.userData?.roles?.[0]?.id
        ? Number(this.userData.roles[0].id)
        : null
    },
    buildAbilityForRole(roleId, permissionsStrings) {
      // Keep the same ACL semantics used in Login-1.vue/changeRoute().
      let abilityPayload = []

      const isSuperAdmin = Number(roleId) === 1
      const isInsuranceAgent = Number(roleId) === 7
      const isAdminAgent = Number(roleId) === 8

      if (isSuperAdmin) {
        abilityPayload = [{ action: 'manage', subject: 'all' }]
      } else if (isInsuranceAgent) {
        abilityPayload = [{ action: 'read', subject: 'insuranceAgentDashboard' }]
      } else if (isAdminAgent) {
        abilityPayload = [{ action: 'read', subject: 'adminAgentDashboard' }]
      } else {
        const permisosTotal = Array.isArray(permissionsStrings)
          ? permissionsStrings
              .filter((item) => typeof item === 'string')
              .map((item) => ({
                action: item.split('.')[1],
                subject: item.split('.')[0]
              }))
          : []

        abilityPayload = permisosTotal
      }

      // Always ensure Auth read.
      if (!abilityPayload.some((permission) => permission.subject === 'Auth')) {
        abilityPayload.push({
          action: 'read',
          subject: 'Auth'
        })
      }

      return abilityPayload
    },
    getDefaultRouteNameByRoleId(roleId) {
      const id = Number(roleId)

      if (id === 7) return 'insurance-agent-dashboard'
      if (id === 8) return 'admin-agent-dashboard'
      if (id === 10) return 'member-dashboard'
      if (id === 9) return 'agent-plan-dashboard'

      // For staff roles (OMA/nurse/frontdesk) use /dashboard, while superadmin/admin use /dashboard-all.
      if (id === 3 || id === 4 || id === 5) return 'dashboard'
      return 'dashboard-all'
    },
    normalizePermissionsResponse(response) {
      // Expected backend contract: { permissions: string[] }
      if (!response) return []

      if (Array.isArray(response)) return response
      if (Array.isArray(response.permissions)) return response.permissions
      if (Array.isArray(response.data?.permissions)) return response.data.permissions

      return []
    },
    async onRoleSelected(nodeOrEvent) {
      if (this.isLoading) return

      // treeselect @select pasa el nodo { id, label }; b-form-select pasaba event
      const nextRoleId = Number(
        nodeOrEvent?.id ?? nodeOrEvent?.target?.value ?? nodeOrEvent ?? this.selectedRoleId
      )
      if (!nextRoleId) return

      const storedUser = this.safeGetUserData()
      if (!storedUser || !Array.isArray(storedUser.roles)) return

      const selectedRole = storedUser.roles.find((r) => Number(r.id) === nextRoleId)
      if (!selectedRole) return

      this.isLoading = true

      try {
        // Reorder roles so UI code that uses roles[0].id behaves as expected.
        const reorderedRoles = [
          selectedRole,
          ...storedUser.roles.filter((r) => Number(r.id) !== nextRoleId)
        ]

        // For non-special roles, we need permissions from backend to rebuild CASL ability.
        const isSuperAdmin = nextRoleId === 1
        const isInsuranceAgent = nextRoleId === 7
        const isAdminAgent = nextRoleId === 8

        let permissionsStrings = []
        if (!isSuperAdmin && !isInsuranceAgent && !isAdminAgent) {
          const response = await axiosR.permissionsByRoleId(nextRoleId)
          permissionsStrings = this.normalizePermissionsResponse(response)
        }

        const abilityPayload = this.buildAbilityForRole(nextRoleId, permissionsStrings)

        const updatedUserData = {
          ...storedUser,
          roles: reorderedRoles,
          role: selectedRole.name,
          ability: abilityPayload
        }

        localStorage.setItem('userData', JSON.stringify(updatedUserData))
        if (this.$ability?.update) {
          this.$ability.update(abilityPayload)
        }

        // Some roles do not use facility headers; clear facility-related keys.
        const isFacilityFreeRole = [7, 8, 9, 10].includes(nextRoleId)
        if (isFacilityFreeRole) {
          localStorage.removeItem('facility')
          localStorage.removeItem('Facility')
        }

        // Notify other layout parts (menu/labels) to recompute from localStorage.
        window.dispatchEvent(new Event('role-changed'))

        const nextRouteName = this.getDefaultRouteNameByRoleId(nextRoleId)
        this.$router.replace({ name: nextRouteName }).catch(() => {})
      } catch (error) {
        this.$bvToast.toast('No fue posible cambiar el rol. Intenta nuevamente.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
/* Tamaño reducido (~50%) y texto oscuro en el header */
.header-role-treeselect >>> .vue-treeselect {
  font-size: 0.75rem;
}
.header-role-treeselect >>> .vue-treeselect__control {
  min-height: 28px;
  padding: 2px 8px;
  color: #495057 !important;
}
.header-role-treeselect >>> .vue-treeselect__single-value,
.header-role-treeselect >>> .vue-treeselect__placeholder {
  color: #495057 !important;
  font-size: 0.75rem;
}
.header-role-treeselect >>> .vue-treeselect__dropdown-arrow-wrapper {
  padding: 2px 4px;
}
</style>

